import { useEffect } from 'react'
import { useProseInstance } from 'providers/ProseInstanceProvider'

import { PropertiesList } from 'common/PropertiesList/PropertiesList'
import { useAssignedProperties } from 'hooks/useAssignedProperties'
import { AssetType, useAlmanacFileSubscription } from 'hooks/useAlmanacFileSubscription'
import {
  AssignedProperty,
  AssignedPropertyValue,
  AssigneeType,
  Property,
  PropertyValue,
} from 'api/clients/PropertyClient'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { OptionSelectVariant } from 'common/OptionSelect/OptionSelect'
import { ButtonVariant } from 'blaze-ui/Button/Types'

interface Props {
  assignee: {
    id: string
    type: AssigneeType
  }
  workspaceId: string
  className?: string
  showEducationTooltip?: boolean
  additionalAssignedProperties?: AssignedProperty[]
  includeDocInfoProperties?: boolean
  setShowSection: (show: boolean) => void
  fitDropdownContent?: boolean
  onUpdate?: () => void
  variant?: OptionSelectVariant
  addPropertyButtonVariant?: ButtonVariant
}

export const SingleAssigneePropertiesContainer = ({
  assignee,
  workspaceId,
  className,
  showEducationTooltip,
  additionalAssignedProperties,
  includeDocInfoProperties,
  setShowSection,
  fitDropdownContent,
  onUpdate,
  variant,
  addPropertyButtonVariant,
}: Props) => {
  const { currentViewRef } = useProseInstance()
  const { refresh: refreshFinder } = useFinderContext()

  const {
    fileAssignedProperties,
    setAssignedProperties,
    docInfoAssignedProperties,
    hasMore,
    loading,
    loadMore,
    addPropertyToAssignee,
    updateAssignedProperty,
    updateProperty,
    removePropertyFromAssignee,
    addPropertyValueToAssignedProperties,
    refreshAssignedProperty,
  } = useAssignedProperties({ assignee, view: currentViewRef.current })

  useEffect(() => {
    loadMore()
  }, [loadMore, assignee, currentViewRef])

  const realTimeAssignedProperties = (propertyId: number, realtimeProperties: AssignedProperty[]) => {
    setAssignedProperties((prev) => {
      if (!realtimeProperties.length) {
        return prev.filter((ap: AssignedProperty) => ap.property.id !== propertyId)
      }

      const assignedProperties = prev.filter((o: any) => o.id !== null)
      const isExistingProperty = assignedProperties.some((ap) => ap.property.id === propertyId)
      if (isExistingProperty) {
        return prev.map((ap) => (ap.property.id !== propertyId ? ap : realtimeProperties[0]))
      } else {
        return [...prev, ...realtimeProperties]
      }
    })
  }

  useAlmanacFileSubscription({
    asset: {
      id: assignee.id,
      type: {
        [AssigneeType.Article]: AssetType.Article,
        [AssigneeType.Project]: AssetType.Project,
        [AssigneeType.Collection]: AssetType.Project,
        [AssigneeType.MediaFile]: AssetType.MediaFile,
        [AssigneeType.Design]: AssetType.Design,
      }[assignee.type],
    },
    onPropertyChange: (propertyId: number, realtimeProperties: AssignedProperty[]) => {
      return realTimeAssignedProperties(propertyId, realtimeProperties)
    },
  })

  const onAddPropertyValueToAssignedProperties = async (
    assignedPropertyId: number | undefined,
    propertyValue: PropertyValue,
  ) => {
    await addPropertyValueToAssignedProperties(assignedPropertyId, propertyValue)
    refreshFinder()
  }

  const onUpdateAssignedProperty = async (
    id: number | undefined,
    property: Property,
    assignedPropertyValue: AssignedPropertyValue[],
  ) => {
    await updateAssignedProperty(id, property, assignedPropertyValue)
    onUpdate?.()
  }

  return (
    <>
      <PropertiesList
        assignee={assignee}
        workspaceId={workspaceId}
        assignedProperties={
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line no-nested-ternary
          additionalAssignedProperties
            ? [...fileAssignedProperties, ...additionalAssignedProperties]
            : includeDocInfoProperties
            ? [...fileAssignedProperties, ...docInfoAssignedProperties]
            : fileAssignedProperties
        }
        variant={variant}
        className={className}
        hasMore={hasMore}
        loading={loading}
        onLoadMore={loadMore}
        onCreateAssignedProperty={addPropertyToAssignee}
        onUpdateAssignedProperty={onUpdateAssignedProperty}
        onUpdateProperty={updateProperty}
        onRemoveAssignedProperty={removePropertyFromAssignee}
        onAddPropertyValueToAssignedProperties={onAddPropertyValueToAssignedProperties}
        onRefreshAssignedProperty={refreshAssignedProperty}
        showEducationTooltip={showEducationTooltip}
        setShowSection={setShowSection}
        withoutBottomMargin={includeDocInfoProperties}
        fitDropdownContent={fitDropdownContent}
        addPropertyButtonVariant={addPropertyButtonVariant}
      />
    </>
  )
}
